import { useMemo } from 'react';
import type { CondicaoPagto, Parcela } from '@azoupvendas/shared';

// AIDEV-NOTE: Hook que calcula parcelas automaticamente baseado no total, condicao de pagamento e data base.
// O algoritmo DEVE ser identico ao do servidor para garantir consistencia.
// Ultima parcela absorve arredondamento — sem esse ajuste 100/3 = 33.33*3 = 99.99 (falta 0.01)

interface UseParcelasCalcParams {
  total: number;
  condicao: CondicaoPagto | null;
  dataBase?: Date;
}

interface UseParcelasCalcResult {
  parcelas: Parcela[];
  totalComCondicao: number;
}

function calcularParcelas(
  total: number,
  condicao: CondicaoPagto,
  dataBase: Date
): { parcelas: Parcela[]; totalComCondicao: number } {
  if (total <= 0 || condicao.qtde_parcelas <= 0) {
    return { parcelas: [], totalComCondicao: 0 };
  }

  // AIDEV-NOTE: Algoritmo exato replicado do servidor:
  // 1. Aplica acrescimo percentual sobre o total
  // 2. Aplica desconto percentual sobre o valor com acrescimo
  // 3. Divide igualmente entre parcelas
  // 4. Ultima parcela absorve diferenca de arredondamento
  const totalComAcrescimo = total * (1 + condicao.acrescimo / 100);
  const totalComDesconto = totalComAcrescimo * (1 - condicao.desconto / 100);
  const n = condicao.qtde_parcelas;
  const valorPorParcela = totalComDesconto / n;
  const valorRound = Number(valorPorParcela.toFixed(2));

  const parcelas: Parcela[] = [];

  for (let i = 0; i < n; i++) {
    const diasOffset = condicao.parcelas[i]?.dias ?? (i + 1) * 30;
    const vencimento = new Date(dataBase);
    vencimento.setDate(vencimento.getDate() + diasOffset);

    // AIDEV-NOTE: Ultima parcela absorve arredondamento para fechar centavos
    const isUltima = i === n - 1;
    const valor = isUltima
      ? Number((totalComDesconto - valorRound * (n - 1)).toFixed(2))
      : valorRound;

    parcelas.push({
      numero: i + 1,
      vencimento: vencimento.toISOString().split('T')[0]!, // YYYY-MM-DD
      valor
    });
  }

  return { parcelas, totalComCondicao: Number(totalComDesconto.toFixed(2)) };
}

export function useParcelasCalc({
  total,
  condicao,
  dataBase
}: UseParcelasCalcParams): UseParcelasCalcResult {
  return useMemo(() => {
    if (!condicao || total <= 0) {
      return { parcelas: [], totalComCondicao: 0 };
    }
    const base = dataBase ?? new Date();
    return calcularParcelas(total, condicao, base);
  }, [total, condicao, dataBase]);
}

// AIDEV-NOTE: Export puro para testes unitarios sem dependencia de React
export { calcularParcelas };
